import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { formatCNPJ } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectBusca } from "@/components/padroes";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle2, Loader2, Lock, Pencil } from "lucide-react";
import type { User } from "@/data/mock";
import { EditarNomeModal } from "@/components/EditarNomeModal";

interface Departamento { id: string; name: string; }

const SEM_DEPARTAMENTO = "__none__";

function cnpjMask(v: string) {
  return v
    .replace(/\D/g, "")
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

const Perfil = () => {
  const { user, refreshUser } = useAuth();

  const [cnpj, setCnpj] = useState(user?.cnpj ?? "");
  const [razaoSocial, setRazaoSocial] = useState(user?.razaoSocial ?? "");
  const [department, setDepartment] = useState(user?.department ?? SEM_DEPARTAMENTO);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [saving, setSaving] = useState(false);
  const [editandoNome, setEditandoNome] = useState(false);

  useEffect(() => {
    api.get<Departamento[]>("/departamentos").then(setDepartamentos).catch(() => {});
  }, []);

  const avatarSrc = user?.avatarUrl ?? null;
  // CLT não tem empresa própria: nada de CNPJ/razão social no perfil.
  const ehClt = user?.vinculo === "clt";

  const initials = (user?.name ?? "")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch<{ user: User }>("/auth/profile", {
        ...(!ehClt && {
          cnpj: cnpj.replace(/\D/g, "") || null,
          razaoSocial: razaoSocial.trim() || null,
        }),
        department: department === SEM_DEPARTAMENTO ? null : department || null,
      });
      await refreshUser();
      toast({ title: "Perfil atualizado com sucesso" });
    } catch (err) {
      toast({ title: "Erro ao salvar", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Mesmos campos que o RH vê como pendência de cadastro (o que o colaborador enxerga aqui).
  const digitos = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");
  const cadastroCompleto =
    !!user?.department?.trim() &&
    (ehClt || (digitos(user?.cnpj).length === 14 && !!user?.razaoSocial?.trim()));

  const apoio = [user?.email, ehClt ? "CLT" : "PJ", user?.department].filter(Boolean).join(" · ");

  return (
    <AppLayout title="Meu Perfil">
      <div className="mx-auto flex max-w-[880px] flex-col gap-4">
        {/* Cabeçalho */}
        <section
          className="flex flex-wrap items-center gap-[18px] rounded-xl p-6 text-white"
          style={{ background: "var(--gradient-dark)" }}
        >
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-[22px] font-bold">
            {avatarSrc ? (
              <img src={avatarSrc} alt={user?.name} className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex items-center gap-1.5 text-xl font-semibold">
              <span className="truncate">{user?.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-white/70 hover:bg-white/10 hover:text-white"
                onClick={() => setEditandoNome(true)}
                aria-label="Editar nome"
              >
                <Pencil className="!size-3.5" />
              </Button>
            </span>
            <span className="truncate text-sm opacity-80">{apoio}</span>
          </div>
          {user && (
            cadastroCompleto ? (
              <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[hsl(142_71%_38%/0.2)] px-3 text-xs font-semibold text-[hsl(142_60%_70%)]">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Cadastro completo
              </span>
            ) : (
              <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-amber-500/20 px-3 text-xs font-semibold text-amber-300">
                <AlertCircle className="h-3.5 w-3.5" />
                Cadastro incompleto
              </span>
            )
          )}
        </section>

        {user && (
          <EditarNomeModal
            userId={user.id}
            nomeAtual={user.name}
            open={editandoNome}
            onClose={() => setEditandoNome(false)}
            onSuccess={() => refreshUser()}
          />
        )}

        {/* Dados pessoais */}
        <section className="flex flex-col gap-4 rounded-xl border bg-card px-6 py-[22px]">
          <h3 className="text-[15px] font-semibold">Dados Pessoais</h3>
          <div className="grid gap-x-4 gap-y-3.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" value={user?.email ?? ""} disabled className="bg-muted-soft disabled:opacity-100 text-muted-foreground" />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="department">Departamento</Label>
              <SelectBusca
                id="department"
                value={department}
                onChange={setDepartment}
                placeholder="Selecione um departamento"
                placeholderBusca="Buscar departamento..."
                opcoes={[
                  { value: SEM_DEPARTAMENTO, label: "Sem departamento", neutra: true },
                  ...departamentos.map((d) => ({ value: d.name, label: d.name })),
                ]}
              />
            </div>
          </div>
        </section>

        {/* Dados PJ */}
        {!ehClt && (
          <section className="flex flex-col gap-4 rounded-xl border bg-card px-6 py-[22px]">
            <div className="flex flex-col gap-1">
              <h3 className="text-[15px] font-semibold">Dados da Empresa (PJ)</h3>
              <p className="text-[13px] text-muted-foreground">Usados na validação automática das suas NFs.</p>
            </div>
            <div className="grid gap-x-4 gap-y-3.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cnpj">CNPJ próprio</Label>
                <Input
                  id="cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(cnpjMask(e.target.value))}
                  placeholder="00.000.000/0001-00"
                  maxLength={18}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="razao">Razão Social</Label>
                <Input
                  id="razao"
                  value={razaoSocial}
                  onChange={(e) => setRazaoSocial(e.target.value)}
                  placeholder="Nome da sua empresa"
                />
              </div>

              {user?.cnpjContratante && (
                <div className="col-span-full flex flex-col gap-1.5">
                  <Label>CNPJ Contratante (vinculado pelo RH)</Label>
                  <div className="flex h-10 items-center gap-2 rounded-md border bg-muted-soft px-3 text-sm text-muted-foreground">
                    <Lock className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {[user.tomadorNome, formatCNPJ(user.cnpjContratante)].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="h-10 px-5">
            {saving && <Loader2 className="animate-spin" />}
            Salvar alterações
          </Button>
        </div>
      </div>
    </AppLayout>
  );
};

export default Perfil;
